import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ClientLayout } from "../components/ClientLayout";
import { ClientOrderCard } from "../components/ClientOrderCard";
import { clientApi, type ClientOrder } from "../services/clientApi.service";

/**
 * Historique des commandes du client connecté.
 *
 * Tri décroissant par date de livraison. Pagination 20 par page.
 */
export function ClientOrdersPage(): JSX.Element {
  const [orders, setOrders] = useState<ClientOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const result = await clientApi.listOrders({ page, limit: 20 });
        if (cancelled) return;
        setOrders((prev) => (page === 1 ? result.data : [...prev, ...result.data]));
        if (result.total != null && result.limit != null) {
          setHasMore(page * result.limit < result.total);
        } else {
          setHasMore(result.data.length === 20);
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger vos commandes.";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [page]);

  return (
    <ClientLayout title="Mes commandes">
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Mes commandes
        </Typography>

        {error ? <Alert severity="error">{error}</Alert> : null}

        <Paper sx={{ p: 2, overflow: "hidden" }}>
          {loading && orders.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : orders.length === 0 ? (
            <Box sx={{ textAlign: "center" }}>
              <Typography color="text.secondary" sx={{ mb: 1.5 }}>
                Vous n'avez pas encore passé de commande.
              </Typography>
              <Button
                component={RouterLink}
                to="/client/order/new"
                variant="contained"
                color="secondary"
              >
                Commander un repas
              </Button>
            </Box>
          ) : (
            <Stack spacing={0} divider={<Divider flexItem />}>
              {orders.map((order) => (
                <ClientOrderCard key={order.id} order={order} showYearInDate />
              ))}
            </Stack>
          )}
        </Paper>

        {hasMore ? (
          <Button onClick={() => setPage((p) => p + 1)} disabled={loading}>
            {loading ? "Chargement…" : "Charger plus"}
          </Button>
        ) : null}
      </Stack>
    </ClientLayout>
  );
}
